import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchBidServices, type BidServicesKind } from '../detailApi';

const DEFAULT_ERROR = 'Не удалось выполнить запрос';

/** Every query of one bid shares the `['bid', bidId]` prefix so a single action refreshes the whole page. */
export const bidKey = (bidId: number, ...rest: Array<string | number>) => ['bid', bidId, ...rest];

export const errorMessage = (error: unknown) =>
  error instanceof Error && error.message ? error.message : DEFAULT_ERROR;

/** Operator action on a bid: runs the request, then refetches the bid, its offers and history. */
export function useBidAction<Vars>(
  bidId: number,
  action: (vars: Vars) => Promise<unknown>,
  onSuccess?: () => void,
) {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: action,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: bidKey(bidId) });
      onSuccess?.();
    },
  });

  return {
    run: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.isError ? errorMessage(mutation.error) : '',
    reset: mutation.reset,
  };
}

export function useBidServices<T extends Record<string, unknown>>(
  kind: BidServicesKind,
  bidId: number,
  page: number,
) {
  return useQuery({
    queryKey: bidKey(bidId, 'services', kind, page),
    queryFn: () => fetchBidServices<T>(kind, bidId, page),
  });
}
